import { Download } from "lucide-react";

function downloadJson(filename, data) {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function EvidencePack({ run }) {
    if (!run?.evidence_pack) return null;
    const ep = run.evidence_pack;
    const filename = `evidence-pack_${run.scenario_slug}_${run.id.slice(0, 8)}.json`;

    return (
        <section className="tile rounded-sm" data-testid="evidence-pack">
            <header className="flex items-center justify-between p-6 border-b border-slate-200 gap-4">
                <div>
                    <div className="overline mb-1">Evidence pack</div>
                    <h3 className="text-xl font-bold tracking-tight">
                        What was requested, decided, shared and withheld
                    </h3>
                    {ep.sector && (
                        <p
                            className="mt-2 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500"
                            data-testid="evidence-pack-sector"
                        >
                            Sector preset · {ep.sector.label || ep.sector.id}
                        </p>
                    )}
                </div>
                <button
                    className="btn-ghost text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2 shrink-0"
                    onClick={() =>
                        downloadJson(filename, {
                            ...ep,
                            receipt: run.receipt,
                            run_id: run.id,
                        })
                    }
                    data-testid="evidence-pack-download-btn"
                >
                    <Download size={14} strokeWidth={1.5} /> Download JSON
                </button>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                <Block label="Requested" value={ep.requested?.summary} />
                <Block
                    label="Human decision"
                    value={
                        <>
                            <span className="font-semibold">{ep.human_decision?.decision_label}</span>
                            <span className="block text-xs text-slate-500 mt-1">
                                Accountable role: {ep.human_decision?.accountable_role}
                            </span>
                        </>
                    }
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-200 border-t border-slate-200">
                <List label="Shared evidence (refs only)" items={ep.shared_to_katlas?.shared_evidence_refs} tone="ok" />
                <List label="Withheld evidence" items={ep.withheld_from_katlas} tone="warn" />
                <List label="Not sent to KATLAS node" items={ep.not_sent_to_node} tone="muted" italic />
            </div>

            <div className="p-6 border-t border-slate-200 surface-subtle">
                <div className="overline mb-3">AI advisories (advisory only)</div>
                <ul className="space-y-3 text-sm text-slate-700">
                    {(ep.ai_advisories_advisory_only || []).map((a) => (
                        <li key={a.step} className="border-l-2 border-slate-300 pl-3">
                            <div className="font-semibold">{a.step}</div>
                            <div className="text-xs text-slate-500 italic">{a.label}</div>
                            {a.risk_category && (
                                <div className="mt-1">
                                    <span className="kbd">risk</span> {a.risk_category}
                                </div>
                            )}
                            {a.suggested_controls && (
                                <ul className="mt-1 list-disc pl-5 text-slate-600">
                                    {a.suggested_controls.map((c) => <li key={c}>{c}</li>)}
                                </ul>
                            )}
                            {a.suggested_response && (
                                <ul className="mt-1 list-disc pl-5 text-slate-600">
                                    {a.suggested_response.map((c) => <li key={c}>{c}</li>)}
                                </ul>
                            )}
                            {a.watch_outs && (
                                <ul className="mt-1 list-disc pl-5 text-slate-600">
                                    {a.watch_outs.map((c) => <li key={c}>{c}</li>)}
                                </ul>
                            )}
                            {a.note && (
                                <div className="text-xs text-slate-500 mt-1 italic">
                                    {a.note}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

function Block({ label, value }) {
    return (
        <div className="p-6">
            <div className="overline mb-2">{label}</div>
            <div className="text-sm text-slate-800 leading-relaxed">{value}</div>
        </div>
    );
}

function List({ label, items, tone, italic }) {
    const color =
        tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-500";
    return (
        <div className="p-6">
            <div className={`overline mb-3 ${color}`}>{label}</div>
            <ul className={`space-y-1.5 text-sm text-slate-700 ${italic ? "italic" : ""}`}>
                {(items || []).map((it) => (
                    <li key={it} className="flex gap-2">
                        <span className="text-slate-400">·</span>
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
